import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {PreviewLine, PreviewToken} from '../file-preview.model';

function lineFields(line: PreviewLine): PreviewLine[] {
    let field: PreviewToken[] = [];
    const fields: PreviewLine[] = [field];
    for (const token of line) {
        field.push(token);
        if (token.role === 'separator') {
            field = [];
            fields.push(field);
        }
    }
    return fields;
}

@Component({
    selector: 'app-preview-lines',
    standalone: true,
    template: `
        <div class="preview-lines font-monospace" [style.grid-template-columns]="columns()">
            @for (line of rows(); track $index) {
                <div class="preview-row">
                    <span
                        class="preview-number text-end user-select-none pe-2"
                        aria-hidden="true"
                        >{{ $index + 1 }}</span
                    >
                    @for (field of line; track $index) {
                        <span class="preview-field" [class.last-field]="$last">
                            @for (token of field; track $index) {
                                <span [class]="'token-' + token.role">{{ token.text }}</span>
                            }
                        </span>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        :host {
            --token-punctuation: #8b90ad;
            --token-text: #a3541c;
            --token-number: #0d7a52;
            --token-date: #5e3fa0;
            --token-missing: #0b57c2;
        }

        .preview-lines {
            display: grid;
            width: max-content;
            min-width: 100%;
        }

        .preview-row {
            display: grid;
            grid-column: 1 / -1;
            grid-template-columns: subgrid;
            height: var(--preview-row-height);
            border-bottom: 1px solid var(--preview-hairline);
            line-height: calc(var(--preview-row-height) - 1px);
        }

        .preview-number {
            position: sticky;
            left: 0;
            border-right: 1px solid var(--preview-hairline);
            background-color: var(--preview-gutter);
            box-shadow: 0.625rem 0 0 var(--bs-white);
            color: rgba(var(--bs-primary-rgb), 0.45);
            font-size: 0.75rem;
            font-variant-numeric: tabular-nums;
        }

        .preview-field {
            padding-right: 1ch;
            font-size: 0.8125rem;
            white-space: pre;
        }

        .preview-number + .preview-field {
            padding-left: 0.625rem;
        }

        .preview-field.last-field {
            padding-right: 0.625rem;
        }

        .token-punctuation,
        .token-separator {
            color: var(--token-punctuation);
        }

        .token-text {
            color: var(--token-text);
        }

        .token-number {
            color: var(--token-number);
        }

        .token-date {
            color: var(--token-date);
        }

        .token-missing {
            color: var(--token-missing);
            font-style: italic;
        }

        .token-heading,
        .token-fieldName {
            color: var(--bs-primary);
            font-weight: 700;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewLinesComponent {
    readonly lines = input.required<ReadonlyArray<PreviewLine>>();

    protected readonly rows = computed(() => this.lines().map(lineFields));

    protected readonly columns = computed(() => {
        const fieldCount = Math.max(1, ...this.rows().map(fields => fields.length));
        return [
            'var(--preview-gutter-width)',
            ...Array<string>(fieldCount - 1).fill('auto'),
            'minmax(auto, 1fr)',
        ].join(' ');
    });
}
